import { ProjectModel, useCancelProject } from 'api-hooks/projects';
import { AuthorizationRules } from 'common/constants';
import { ProjectStatusEnum } from 'common/constants/enum';
import notification from 'components/common/notification';
import { PopoverMenuType } from 'components/popover';
import useApprovalAction from 'hooks/use-approval-action';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useDismiss from 'hooks/use-dismiss';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

export default function useProjectCancelAction(project: ProjectModel) {
  const { t } = useTranslation();
  const onDismiss = useDismiss('projects');
  const { onRequestApproval } = useApprovalAction('projects');
  const cancelMutate = useCancelProject();
  const dialog = useDialog();
  const projectId = project.id;
  const status = project.status;
  const { can } = useAuthorization();

  const isAllowed = React.useMemo(() => {
    switch (status) {
      case ProjectStatusEnum.Active:
      case ProjectStatusEnum.Completed:
        return true;
      default:
        return false;
    }
  }, [status]);

  const cancelPermission = isAllowed && can(AuthorizationRules.ProjectsCancel);

  const onCancel = React.useCallback(async () => {
    dialog.showConfirmation({
      message: t('ticketSales:confirmation_cancel_project'),
      title: t('ticketSales:cancel_project'),
      onPositiveAction: async (onClose) => {
        try {
          const result = await cancelMutate.mutateAsync({
            projectId,
          });

          notification.success({ message: result?.message });
          onDismiss(projectId);
          onClose();

          onRequestApproval(projectId);
        } catch (error) {
          notification.error({ message: error.message });
        }
      },
      onNegativeAction: (onClose) => {
        onClose();
      },
    });
  }, [cancelMutate, dialog, onDismiss, onRequestApproval, projectId, t]);

  const cancelAction: PopoverMenuType = {
    key: `cancel-${projectId}`,
    label: t('common:cancel_project'),
    onClick: onCancel,
    isAllowed: cancelPermission,
    isError: true,
  };

  return {
    onCancel,
    cancelPermission,
    cancelAction,
  };
}
